import { useState } from 'react';
import { Button, Dialog, Input, Select } from '@artosera/ui';
import { estFormatCartel, FORMATS_ETIQUETTE, type FormatEtiquette } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import { reserveApi, useMutationReserve, type CibleEtiquette } from '../../api/reserve.js';
import { enregistrerFichier } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from './InventairePage.module.css';

export function Etiquettes({ cibles }: { cibles: CibleEtiquette[] }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [format, setFormat] = useState<FormatEtiquette>('rouleau-62x29');
  const [texteLibre, setTexteLibre] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [regeneration, setRegeneration] = useState(false);
  const pdf = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.pdfEtiquettes(
      galerieSlug,
      { cibles, format, texteLibre },
      i18n.resolvedLanguage ?? 'fr',
      appel,
    ),
  );
  const regenerer = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.regenerer(galerieSlug, cibles[0]!, appel),
  );
  const erreur = pdf.error ?? regenerer.error;
  return (
    <div className={styles.formulaire}>
      <Button variant="secondary" onClick={() => setOuvert(true)} disabled={!cibles.length}>
        {t('regie:inventaire.imprimer')}
      </Button>
      {cibles.length === 1 && galerieALaction(galerie, 'inventaire:manage') && (
        <Button variant="secondary" onClick={() => setRegeneration(true)}>
          {t('regie:inventaire.regenerer')}
        </Button>
      )}
      <Dialog
        open={ouvert}
        onOpenChange={setOuvert}
        title={t('regie:inventaire.imprimer')}
        closeLabel={t('commun:actions.fermer')}
      >
        <div className={styles.formulaire}>
          <Field htmlFor="etiquettes-format" label={t('regie:inventaire.format')}>
            <Select
              id="etiquettes-format"
              value={format}
              onValueChange={(v) => setFormat(v as FormatEtiquette)}
              options={FORMATS_ETIQUETTE.filter((f) => !estFormatCartel(f)).map((f) => ({
                value: f,
                label: t(`regie:inventaire.formats.${f}`),
              }))}
            />
          </Field>
          <Field htmlFor="etiquettes-texte" label={t('regie:inventaire.texteLibre')}>
            <Input
              id="etiquettes-texte"
              maxLength={200}
              value={texteLibre}
              onChange={(e) => setTexteLibre(e.target.value)}
            />
          </Field>
          <Button
            loading={pdf.isPending}
            onClick={() =>
              pdf.mutate(undefined, {
                onSuccess: (fichier) => enregistrerFichier(fichier, 'etiquettes.pdf'),
              })
            }
          >
            {t('regie:inventaire.telecharger')}
          </Button>
          {pdf.error && (
            <p role="alert">
              {estApiError(pdf.error) ? pdf.error.message : t('commun:erreurReseau')}
            </p>
          )}
        </div>
      </Dialog>
      <Dialog
        open={regeneration}
        onOpenChange={setRegeneration}
        title={t('regie:inventaire.regenerer')}
        description={t('regie:inventaire.regenererConfirmation')}
        closeLabel={t('commun:actions.fermer')}
      >
        <Button
          loading={regenerer.isPending}
          onClick={() => regenerer.mutate(undefined, { onSuccess: () => setRegeneration(false) })}
        >
          {t('regie:inventaire.confirmer')}
        </Button>
        {erreur && (
          <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
        )}
      </Dialog>
    </div>
  );
}
